import type { Placement } from '@popperjs/core';
import { useEffect, useState } from 'react';
import { MdAddCircle } from 'react-icons/md';
import ReactTooltip from 'react-tooltip';
import BtnPopover from '../../components/btn-popover';
import { useHotel } from '../../context/hotel-context';
import { useUser } from '../../context/user-context';
import type { Hotel, Maybe } from '../../graphql/types';
import { SectionContainer } from '../reports/components/section-container';
import { EmailTableSection } from './components/email-table-section';
import HeatMap from './components/heat-map';
import HotelWidget from './components/hotel-widget';
import {
  useAddHotelToPortfolioMutation,
  useCreatePortfolioMutation,
} from './gql/_gen_/portfolio.gql';

type AddHotelButtonProps = {
  portfolioId?: string;
  userId?: string | null;
};

type AddHotelPanelProps = {
  close?: () => void;
  portfolioId?: string;
  userId?: string | null;
};

function AddHotelPanel({ close, portfolioId, userId }: AddHotelPanelProps) {
  const [newHotel, setNewHotel] = useState<string>('');
  const [inputVal, setInputVal] = useState<string>('');
  const [addHotel] = useAddHotelToPortfolioMutation();
  const [createPortfolio] = useCreatePortfolioMutation();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (newHotel.length === 5) {
      if (portfolioId) {
        await addHotel({
          variables: {
            brandCode: newHotel,
            portfolioId: portfolioId || '',
          },
        });
        close?.();
        setNewHotel('');
      } else if (userId) {
        await createPortfolio({
          variables: {
            portfolio: {
              user_id: userId,
            },
          },
          onCompleted: (data) => {
            addHotel({
              variables: {
                brandCode: newHotel,
                portfolioId: data?.createPortfolio?.id || '',
              },
            });
          },
        });
        close?.();
        setNewHotel('');
      }
    } else {
      setInputVal('Brand Code must be 5 characters');
    }
  };

  const handleChange = (value: string) => {
    if (inputVal.length) {
      setInputVal('');
    }
    setNewHotel(value.replace(/\D/g, ''));
  };

  return (
    <div className='flex flex-col px-4 pt-2 pb-4 bg-white border border-gray-200 rounded shadow'>
      <form onSubmit={handleSubmit} className='flex flex-col'>
        <label className='text-sm font-semibold' htmlFor='brandCode'>
          Add Hotel:
        </label>
        {inputVal.length ? <p className='text-red-500 text-xs'>{inputVal}</p> : null}
        <input
          className='text-sm p-1'
          type='text'
          id='brandCode'
          placeholder='Brand Code'
          onChange={(e) => handleChange(e.target.value)}
          value={newHotel}
          maxLength={5}
        />
      </form>
    </div>
  );
}

function AddHotelButton({ portfolioId, userId }: AddHotelButtonProps) {
  const btnProps = {
    btnIcon: MdAddCircle,
    btnIconClass: 'text-gray-200',
    btnTooltip: 'Add Hotel',
    btnClass: 'mt-1.5',
    btnSpanClass: 'h-full',
    popPlacement: 'right' as Placement,
    offsetV: 20,
    offsetH: 5,
    panelContent: (props?: { close?: () => void }) => (
      <AddHotelPanel close={props?.close} portfolioId={portfolioId} userId={userId} />
    ),
  };
  return (
    <>
      <BtnPopover {...btnProps} />
      <ReactTooltip />
    </>
  );
}

function Portfolio() {
  const { user } = useUser();
  const hotels = user?.portfolio?.hotels;
  const portfolioId = user?.portfolio?.id;
  const [selectedHotel, setSelectedHotel] = useState<Maybe<Hotel>>(null);
  const { hotel, setBrandCode } = useHotel();

  useEffect(() => {
    if (hotels && hotels.length > 0) {
      const selectedHotel = hotels.find((h) => h?.brand_code === hotel?.brand_code);
      setSelectedHotel(selectedHotel || hotels[0]);
    }
  }, [hotels, hotel]);

  const handleSelect = (hotel?: Hotel | null) => {
    if (hotel) {
      setSelectedHotel(hotel);
      setBrandCode(hotel.brand_code);
    }
  };

  const heatMapHeader = () => (
    <span data-tip={selectedHotel?.name}>{selectedHotel?.brand_code} Heat Map</span>
  );

  return (
    <div className='p-4 flex flex-col gap-4'>
      <SectionContainer
        title='Portfolio'
        toolbar={<AddHotelButton portfolioId={portfolioId} userId={user?.id} />}
      >
        <div className='flex flex-wrap'>
          {hotels?.map((hotel) => (
            <HotelWidget
              key={hotel?.brand_code}
              data={hotel}
              selected={hotel?.brand_code === selectedHotel?.brand_code}
              portfolioId={portfolioId}
              setSelected={handleSelect}
            />
          ))}
        </div>
      </SectionContainer>

      <SectionContainer title={heatMapHeader()}>
        <HeatMap brandCode={selectedHotel?.brand_code} />
      </SectionContainer>

      {console.log(
        'Portfolio selectedHotel timezone:',
        selectedHotel?.brand_code,
        selectedHotel?.timezone,
      )}
      <EmailTableSection brandCode={selectedHotel?.brand_code} timezone={selectedHotel?.timezone} />

      <ReactTooltip />
    </div>
  );
}

export default Portfolio;
